import { useTranslation } from "react-i18next";
import { SelectItem } from "@skailer-com/uikit";
import {
  ApiImportDataType,
  ApiImportErrorCode,
  ApiImportGlobalUpdateBehavior,
  ApiImportParsePossibleEntity,
  ApiImportFieldUpdateBehavior,
  ApiImportNewValueBehavior,
} from "@/types/import-data.types.ts";

export function useImportDataOptions() {
  const { t } = useTranslation();

  const getImportDataTypeOptions = (): SelectItem[] => [
    {
      id: ApiImportDataType.employee,
      title: t("custom_fields:settings_title"),
    },
    {
      id: ApiImportDataType.custom_table,
      title: t("custom_fields:custom_table"),
    },
    {
      id: ApiImportDataType.time_off_history,
      title: t("reporting:time_off_history_report_title"),
    },
    {
      id: ApiImportDataType.time_off_balances,
      title: t("core:time_off_balances"),
    },
  ];

  const getImportBehaviorOptions = (): SelectItem[] => [
    {
      id: ApiImportGlobalUpdateBehavior.update_only,
      title: t("import_data:behavior_update_only"),
      caption: t("import_data:behavior_update_only_caption"),
    },
    {
      id: ApiImportGlobalUpdateBehavior.create_only,
      title: t("import_data:behavior_create_only"),
      caption: t("import_data:behavior_create_only_caption"),
    },
    {
      id: ApiImportGlobalUpdateBehavior.upsert,
      title: t("import_data:behavior_upsert"),
      caption: t("import_data:behavior_upsert_caption"),
    },
  ];

  const getUpdateBehaviorOptions = (): SelectItem[] => [
    {
      id: ApiImportFieldUpdateBehavior.only_if_empty,
      title: t("import_data:update_only_if_empty"),
    },
    {
      id: ApiImportFieldUpdateBehavior.always,
      title: t("import_data:update_always"),
    },
  ];

  const getNewValuesBehaviorOptions = (): SelectItem[] => [
    {
      id: ApiImportNewValueBehavior.create_new_values,
      title: t("import_data:create_new_values"),
      caption: t("import_data:create_new_values_caption"),
    },
    {
      id: ApiImportNewValueBehavior.skip_news_records,
      title: t("import_data:skip_new_records"),
      caption: t("import_data:skip_new_records_caption"),
    },
  ];

  const getEmployeeFieldTitle = (key: string, defaultTitle?: string | undefined): string => {
    switch (key) {
      case "first_name":
        return t("core:first_name");
      case "last_name":
        return t("core:last_name");
      case "middle_name":
        return t("core:middle_name");
      case "email":
        return t("core:email");
      case "email_personal":
        return t("core:email_personal");
      case "phone":
        return t("core:phone");
      case "phone_personal":
        return t("core:phone_personal");
      case "date_birth":
        return t("core:birth_date");
      case "gender":
        return t("core:gender");
      case "personnel_number":
        return t("core:employee_number");
      case "termination_date":
        return t("core:termination_date");
      case "name_pronunciation":
        return t("common:name_pronunciation");
      case "address_1":
        return t("core:address_line_1");
      case "address_2":
        return t("core:address_line_2");
      case "state":
        return t("core:state");
      case "city":
        return t("core:city");
      case "zip":
        return t("core:zip");
      case "country":
        return t("core:country");
      case "job_profile":
        return t("core:job_profile");
      case "position_date_from":
        return t("core:position_effective_date");
      case "department":
        return t("core:department");
      case "division":
        return t("core:division");
      case "legal_entity":
        return t("core:legal_entity");
      case "location":
        return t("common:location");
      case "location_address":
        return t("core:address");
      case "reporting_to":
        return t("core:manager");
      case "employment_date_from":
        return t("core:employment_start_date");
      case "employment_date_to":
        return t("core:employment_end_date");
      case "employment_type":
        return t("core:employment_type");
      case "employment_work_pattern":
        return t("core:work_pattern");
      case "employment_contract":
        return t("core:employment_contract");
      case "employment_probation_date_till":
        return t("core:probation_till");
      case "salary_gross":
        return t("core:base_salary_gross");
      case "salary_net":
        return t("core:base_salary_net");
      case "salary_currency":
        return t("common:currency");
      case "compensation_change_reason":
        return t("core:compensation_change_reason");
      case "compensation_effective_date":
        return t("core:compensation_date");
      case "compensation_period":
        return t("common:period");
      case "employee_id":
        return t("core:employee_id");
      case "date_hired":
        return t("core:hire_date");
      case "manager_email":
        return t("core:manager") + ": " + t("core:email").toLowerCase();
      case "position_comment":
        return t("common:comments");
      case "position_change_reason":
        return t("core:employee_position_change_reason");
      default:
        return defaultTitle ?? key;
    }
  };

  const getTimeoffBalanceFieldTitle = (key: string, defaultTitle?: string | undefined): string => {
    switch (key) {
      case "employee":
        return t("core:employee");
      case "balance":
        return t("time_off:balance");
      case "category":
        return t("time_off:category");
      default:
        return defaultTitle ?? key;
    }
  };

  const getTimeoffHistoryFieldTitle = (key: string, defaultTitle?: string | undefined): string => {
    switch (key) {
      case "employee":
        return t("core:employee");
      case "date_from":
        return t("common:date_from");
      case "date_to":
        return t("common:date_to");
      case "balance":
        return t("time_off:used_amount");
      case "category":
        return t("time_off:category");
      default:
        return defaultTitle ?? key;
    }
  };

  const getImportDataFieldTitle = (
    importType: ApiImportDataType,
    entity: ApiImportParsePossibleEntity
  ): string => {
    switch (importType) {
      case ApiImportDataType.custom_table:
        return entity.title;
      case ApiImportDataType.employee:
        return entity.is_custom
          ? entity.title
          : getEmployeeFieldTitle(entity.field_key, entity.title);
      case ApiImportDataType.time_off_history:
        return entity.is_custom
          ? entity.title
          : getTimeoffHistoryFieldTitle(entity.field_key, entity.title);
      case ApiImportDataType.time_off_balances:
        return entity.is_custom
          ? entity.title
          : getTimeoffBalanceFieldTitle(entity.field_key, entity.title);
      default:
        return entity.title;
    }
  };

  const getFieldGroupTitle = (group: string | undefined): string | undefined => {
    if (!group) return undefined;
    switch (group) {
      case "possible_employee_fields":
        return t("core:personal_information");
      case "possible_employee_position_fields":
        return t("core:position");
      case "possible_employee_salary_fields":
        return t("core:compensation");
      case "possible_employee_custom_fields":
        return t("custom_fields:custom_fields");
      default:
        return undefined;
    }
  };

  const getImportDataTypeTitle = (type: ApiImportDataType): string => {
    switch (type) {
      case ApiImportDataType.employee:
        return t("core:employees");
      case ApiImportDataType.compensation:
        return t("core:compensation");
      case ApiImportDataType.additional_compensation:
        return t("core:additional_compensation");
      case ApiImportDataType.position:
        return t("core:positions");
      case ApiImportDataType.employment:
        return t("core:employment");
      case ApiImportDataType.custom_table:
        return t("custom_fields:custom_table");
      case ApiImportDataType.time_off_balances:
        return t("core:time_off_balances");
      case ApiImportDataType.time_off_history:
        return t("reporting:time_off_history_report_title");
      default:
        return type;
    }
  };

  const getImportBehaviorTitle = (behavior: ApiImportGlobalUpdateBehavior): string => {
    switch (behavior) {
      case ApiImportGlobalUpdateBehavior.create_only:
        return t("import_data:behavior_create_only").toLowerCase();
      case ApiImportGlobalUpdateBehavior.update_only:
        return t("import_data:behavior_update_only").toLowerCase();
      case ApiImportGlobalUpdateBehavior.upsert:
        return t("import_data:behavior_upsert").toLowerCase();
      default:
        return behavior;
    }
  };

  const getMatchSummary = (params: {
    importType: ApiImportDataType;
    customTableName?: string;
    matchingByLabel: string;
    importBehavior: ApiImportGlobalUpdateBehavior;
  }): { i18nKey: string; values: Record<string, string> } => {
    const { importType, customTableName, matchingByLabel, importBehavior } = params;

    let importTypeTitle = getImportDataTypeTitle(importType);
    if (importType === ApiImportDataType.custom_table && customTableName) {
      importTypeTitle = `${importTypeTitle}: ${customTableName}`;
    }

    return {
      i18nKey: "import_data:match_summary",
      values: {
        import_type: importTypeTitle,
        match_by: matchingByLabel,
        behavior: getImportBehaviorTitle(importBehavior),
      },
    };
  };

  const getErrorCodeDescription = (errorCode: ApiImportErrorCode): string => {
    switch (errorCode) {
      case ApiImportErrorCode.employee_not_found:
        return t("import_data:error_employee_not_found");
      case ApiImportErrorCode.employee_identifier_missing:
        return t("import_data:error_employee_identifier_missing");
      case ApiImportErrorCode.employee_already_exists:
        return t("import_data:error_employee_already_exists");
      case ApiImportErrorCode.employee_update_skipped:
        return t("import_data:error_employee_update_skipped");
      case ApiImportErrorCode.position_missing_effective_date:
        return t("import_data:error_position_missing_effective_date");
      case ApiImportErrorCode.employment_missing_effective_date:
        return t("import_data:error_employment_missing_effective_date");
      case ApiImportErrorCode.compensation_missing_effective_date:
        return t("import_data:error_compensation_missing_effective_date");
      case ApiImportErrorCode.custom_table_missing_effective_date:
        return t("import_data:error_custom_table_missing_effective_date");
      case ApiImportErrorCode.invalid_data:
        return t("import_data:error_invalid_data");
      case ApiImportErrorCode.email_already_exists:
        return t("api_errors:email_already_exists");
      case ApiImportErrorCode.required_fields_missing:
        return t("import_data:error_required_fields_missing");
      default:
        return errorCode;
    }
  };

  return {
    getImportDataTypeOptions,
    getImportDataTypeTitle,
    getUpdateBehaviorOptions,
    getImportDataFieldTitle,
    getEmployeeFieldTitle,
    getTimeoffBalanceFieldTitle,
    getTimeoffHistoryFieldTitle,
    getImportBehaviorOptions,
    getImportBehaviorTitle,
    getFieldGroupTitle,
    getNewValuesBehaviorOptions,
    getErrorCodeDescription,
    getMatchSummary,
  };
}
